import React, { createContext, useContext, useState } from "react";
import PropTypes from "prop-types";

/**
 * AnnouncementsModalContext values
 * @typedef {Object} AnnouncementsModalContextType
 * @property {boolean} open - Whether the modal is open
 * @property {string} title - The modal title
 * @property {string} label - The modal label
 * @property {string} content - The modal content
 * @property {string} cancelLabel - The cancel button label
 * @property {Function} cancelAction - The cancel button action
 * @property {Function} action - The main action function
 * @property {Function} showModal - Function to show/hide the modal
 * @property {Function} setValues - Function to set the modal values
 */

const noopValues = {
  open: false,
  title: "",
  label: "",
  content: "",
  cancelLabel: "",
  cancelAction: null,
  action: null,
  showModal: () => {},
  setValues: () => {},
};

export const AnnouncementsModalContext = createContext(noopValues);

export function AnnouncementsModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [modalTitle, setTitle] = useState("");
  const [modalLabel, setLabel] = useState("");
  const [modalAction, setModalAction] = useState(null);
  const [modalContent, setContent] = useState("");
  const [modalCancel, setModalCancel] = useState("");
  const [modalCancelAction, setModalCancelAction] = useState(null);

  const setValues = (args) => {
    const {
      action = null,
      cancelAction = null,
      cancelLabel = "",
      title = "",
      label = "",
      content = "",
    } = args;

    setTimeout(
      () => {
        setTitle(title);
        setLabel(label);
        setContent(content);
        setModalCancel(cancelLabel);
        setModalAction(() => action);
        setModalCancelAction(() => cancelAction);
      },
      !title && !label && !content ? 500 : 1,
    );

    setOpen(false);
  };

  const actions = {
    showModal(show = true) {
      setOpen(show);
    },
    setValues,
  };

  const values = {
    open,
    title: modalTitle,
    label: modalLabel,
    content: modalContent,
    cancelLabel: modalCancel,
    cancelAction: modalCancelAction,
    action: modalAction || setValues,
  };

  return (
    <AnnouncementsModalContext.Provider value={{ ...actions, values }}>
      {children}
    </AnnouncementsModalContext.Provider>
  );
}

/**
 * Hook to access announcements modal context
 * @returns {AnnouncementsModalContextType} Announcements modal context
 */
export function useAnnouncementsModal() {
  const context = useContext(AnnouncementsModalContext);

  if (!context) {
    throw new Error("useAnnouncementsModal must be used within an AnnouncementsModalProvider");
  }

  return context;
}

AnnouncementsModalProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.object]).isRequired,
};
